import { z } from 'zod'

// ---------------------------------------------------------------------------
// Ops health (OPS-013) — the production box's own host checks, as written by
// its cron healthcheck (`pruvato-healthcheck.sh`, every 5 minutes) into this
// console's database and shown on the Health screen. Not tenant data: it is
// about the one box everything runs on, so it is platform-scoped like
// `users`/`sessions`, with no tenant_id.
//
// `heartbeat` is what makes the alert list trustworthy. "No open problems" only
// means "healthy" if the check is still running; a stale or missing heartbeat
// means the alert list cannot be relied on, and the UI says so rather than
// rendering an empty list as good news.
// ---------------------------------------------------------------------------

export const opsAlertSchema = z.object({
  id: z.string(),
  /** Stable machine key for the check, e.g. `backup_stale`, `queue_stuck`. */
  checkKey: z.string(),
  /** Human-readable detail as of the most recent run that reported it. */
  message: z.string(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  /** Null while still open. */
  resolvedAt: z.string().nullable(),
})
export type OpsAlert = z.infer<typeof opsAlertSchema>

export const opsHeartbeatSchema = z.object({
  lastRunAt: z.string(),
  openProblems: z.number().int().nonnegative(),
  /** Informational state that is not a problem, e.g. inbound mail deliberately off. */
  notice: z.string().nullable(),
})
export type OpsHeartbeat = z.infer<typeof opsHeartbeatSchema>

export const opsHealthSchema = z.object({
  /** Null if the healthcheck has never reported. */
  heartbeat: opsHeartbeatSchema.nullable(),
  openAlerts: z.array(opsAlertSchema),
  /** Most recently resolved first, last 7 days. */
  recentlyResolved: z.array(opsAlertSchema),
  asOf: z.string(),
})
export type OpsHealth = z.infer<typeof opsHealthSchema>
